import { Flex, Icon, useToken } from '@chakra-ui/react';
import { FiCheck } from 'react-icons/fi';

/**
 * @useFor any visual checkbox — list-row select, form field, table
 * column header select-all, in-drawer subscription toggles.
 *
 * Visual primitive only — the consumer wires `onClick` / `onChange`
 * on the wrapping element. Keyboard a11y belongs on the wrapper too
 * (set `role="checkbox" aria-checked` + `onKeyDown` for Space/Enter).
 *
 * Examples in-tree:
 *   - DataTable row-select column
 *   - ProtocolRecipientsEditor + DistrictDefaultCellsEditor
 *     (where the wrapping Flex carries the click + a11y)
 *
 * **Do NOT use** for:
 *   - **Bare `<input type="checkbox">`** anywhere in the app — that's
 *     the inline-checkbox anti-pattern the `aegix/no-inline-checkbox`
 *     ESLint rule is designed to catch. Always reach for this component.
 *   - **On/off toggles** for settings (use `toggle` / `ToggleButton`).
 *   - **Tri-state / mixed-state** checkboxes — extend this component
 *     with an `indeterminate` prop instead of rolling a parallel one.
 *
 * See `libs/themes/aegix/CONVENTIONS.md` for the full pattern map.
 */
interface StyledCheckboxProps {
  checked: boolean;
}

export const StyledCheckbox = ({ checked }: StyledCheckboxProps) => {
  const [checkedBorder, uncheckedBorder, checkedBg, uncheckedBg] = useToken(
    'colors',
    ['border.primary', 'border.tertiary', 'defaultSafety.primary', 'surface.input']
  );

  return (
    <Flex justifyContent="center">
      <Flex
        border="1px solid"
        borderColor={checked ? checkedBorder : uncheckedBorder}
        borderRadius="sm"
        w="18px"
        h="18px"
        bg={checked ? checkedBg : uncheckedBg}
        alignItems="center"
        justifyContent="center"
      >
        {checked && <Icon as={FiCheck} color="white" boxSize="14px" />}
      </Flex>
    </Flex>
  );
};
